import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { StoreData } from '../../reducers/typings'
import { loadFilterPreferences } from '@user/store/userPreferences'
import { Duplicate } from '@user/query/useDuplicateCheck'
import { ILocationSearchModel } from '@user/query/useDuplicateLocationSearch'

export type PestPacLocationPageState = {
  page: number
  perPage: number
  orderBy: string
  searchText: string
  resultCount: number
  duplicates: ILocationSearchModel[]
}

const PestPacLocationDuplicateAdapter =
  createEntityAdapter<ILocationSearchModel>()

export const initialState =
  PestPacLocationDuplicateAdapter.getInitialState<PestPacLocationPageState>({
    page: 1,
    perPage: 10,
    orderBy: 'firstName desc',
    duplicates: [],
    resultCount: 0,
    searchText: '',
  })

const PestPacLocationDuplicateSlice = createSlice({
  name: 'PestPacLocationDuplicateSlice',
  initialState,
  reducers: {
    setPestPacLocationDuplicates: (state, { payload }: PayloadAction<any>) => {
      state.page = payload.page || 1
      state.perPage = payload.perPage

      const newDuplicates = payload.duplicates.map((item: Duplicate) => ({
        ...item,
        id: `${item.id}-${crypto.randomUUID()}`,
      }))

      if (state.page === 1) {
        // Fresh load, reset duplicates
        state.duplicates = newDuplicates
      } else {
        // Append only if not already present
        const existingIds = new Set(
          state.duplicates.map((d) => d.id.split('-')[0]),
        )
        const uniqueNewDuplicates = newDuplicates.filter(
          (d) => !existingIds.has(d.id.split('-')[0]),
        )

        state.duplicates = [...state.duplicates, ...uniqueNewDuplicates]
      }

      state.resultCount = payload.resultCount
    },
    setOrderBy: (state, { payload }: PayloadAction<string>) => {
      state.orderBy = payload
    },
    setPage: (state, { payload }: PayloadAction<number>) => {
      state.page = payload
    },
    setPerPage: (state, { payload }: PayloadAction<number>) => {
      state.perPage = payload
    },
    setSearch: (state, { payload }: PayloadAction<string>) => {
      state.searchText = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      const userPref = payload.find(
        (p) => p.type === 'pestPacLocationDuplicates',
      )
      state.orderBy = userPref
        ? JSON.parse(userPref.filter).orderBy
        : 'firstName desc'
      return state
    })
  },
})

export const {
  setOrderBy,
  setPage,
  setPerPage,
  setPestPacLocationDuplicates,
  setSearch,
} = PestPacLocationDuplicateSlice.actions

export default PestPacLocationDuplicateSlice.reducer

const roleSelectors = PestPacLocationDuplicateAdapter.getSelectors<StoreData>(
  (state) => state.pestPacLocationDuplicates,
)

export const { selectAll: selectAllPestPacLocationDuplicates } = roleSelectors

export const selectPestPacLocationDuplicatesFilters = (state: StoreData) => {
  return state.pestPacLocationDuplicates
}
